import React from 'react';
import { motion } from 'framer-motion';
import '../styles/pages/PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      {/* Hero Section */}
      <section className="privacy-hero">
        <div className="privacy-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="privacy-hero-content"
          >
            <div className="hero-badge">
              🔒 Privacy Policy
            </div>
            
            <h1 className="hero-title">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
            
            <p className="hero-subtitle">
              Last Updated: January 1, 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="privacy-content">
        <div className="privacy-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="privacy-text"
          >
            <div className="privacy-section">
              <p className="privacy-intro">
                Welcome to RKube Inc ("we," "us," or "our"). Protecting your privacy is important to us. This Privacy Policy outlines how we collect, use, disclose, and safeguard your personal information in connection with our comprehensive business process outsourcing, financial services, and technology solutions. By using our services, you agree to the terms outlined in this Privacy Policy.
              </p>
            </div>

            <div className="privacy-section">
              <h2>1. Information We Collect</h2>
              
              <h3>1.1 Customer and Client Information</h3>
              <p>We may collect customer and client information, including but not limited to:</p>
              <ul>
                <li>Contact details (name, email address, phone number)</li>
                <li>Company information (industry, size, location)</li>
                <li>Financial information for our bookkeeping, tax preparation, and payroll services</li>
                <li>Employment and staffing-related information</li>
                <li>Technical specifications for our IT and cloud solutions</li>
                <li>Any other information you choose to provide</li>
              </ul>

              <h3>1.2 Non-Personal Information</h3>
              <p>We may automatically collect non-personal information, including:</p>
              <ul>
                <li>IP addresses</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Usage data and analytics</li>
                <li>Website interaction patterns</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>2. How We Use Your Information</h2>
              <p>We use the collected information for the following purposes:</p>
              <ul>
                <li>Providing comprehensive business process outsourcing services including bookkeeping, tax preparation, payroll management, and equity management</li>
                <li>Delivering technology solutions including software development, AI & machine learning, cloud solutions, DevOps, and data analytics</li>
                <li>Facilitating staffing and recruitment services</li>
                <li>Facilitating communication and collaboration between our teams and yours</li>
                <li>Processing transactions and managing client accounts</li>
                <li>Providing customer support and technical assistance</li>
                <li>Improving our services and user experience</li>
                <li>Sending service-related communications and updates</li>
                <li>Complying with legal obligations and regulatory requirements</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>3. Information Sharing</h2>
              <p>We maintain strict confidentiality standards. We do not sell, trade, or rent your personal information to third parties. We may share information only in the following limited circumstances:</p>
              <ul>
                <li>With trusted third-party service providers who assist in delivering our services, bound by confidentiality agreements</li>
                <li>With business partners and affiliates for joint service delivery, where explicitly agreed upon</li>
                <li>When required by law, regulation, or legal process</li>
                <li>To protect our rights, property, or safety, or that of our clients or others</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>4. Security</h2>
              <p>We employ industry-standard security measures to protect your information from unauthorized access, disclosure, alteration, or destruction. Our security measures include:</p>
              <ul>
                <li>Enterprise-grade encryption for data transmission and storage</li>
                <li>Multi-factor authentication and access controls</li>
                <li>Regular security assessments and audits</li>
                <li>Employee training on data protection and privacy</li>
                <li>Secure data centers with physical and digital security controls</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>5. Data Retention</h2>
              <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law or regulatory requirements related to financial services, tax preparation, or other professional services we provide.</p>
            </div>

            <div className="privacy-section">
              <h2>6. Cookies and Similar Technologies</h2>
              <p>We may use cookies and similar technologies to enhance your experience on our website and gather information about your usage patterns. You can control cookie preferences through your browser settings.</p>
            </div>

            <div className="privacy-section">
              <h2>7. Third-Party Links</h2>
              <p>Our website and services may contain links to third-party websites. We are not responsible for the privacy practices or content of these websites. We encourage you to review the privacy policies of any third-party sites you visit.</p>
            </div>

            <div className="privacy-section">
              <h2>8. Your Choices and Rights</h2>
              <p>You have the right to:</p>
              <ul>
                <li>Review and correct your personal information</li>
                <li>Request access to your personal information</li>
                <li>Opt-out of receiving marketing communications from us</li>
                <li>Request the deletion of your personal information, subject to legal and contractual obligations</li>
                <li>Object to the processing of your personal information in certain circumstances</li>
                <li>Request data portability where technically feasible</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>9. Changes to This Privacy Policy</h2>
              <p>We reserve the right to update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on our website and updating the "Last Updated" date. For significant changes, we may provide additional notice through email or other means.</p>
            </div>

            <div className="privacy-section">
              <h2>10. Contact Us</h2>
              <p>If you have any questions or concerns about this Privacy Policy, please contact us at:</p>
              <div className="contact-info">
                <p><strong>Email:</strong> support@rkubeinc.com</p>
                <p><strong>Phone:</strong> (984) 234-7007</p>
                <p><strong>Address:</strong> 4030 Wake Forest Road, STE 349<br />Raleigh, NC 27609, USA</p>
              </div>
            </div>

            <div className="privacy-section">
              <h2>11. Opt-Out of SMS, Email and Other Campaigns</h2>
              <p>We respect your communication preferences:</p>
              <ul>
                <li><strong>SMS Communications:</strong> If you subscribe to our SMS alerts or would like us to remove you from our SMS campaigns, reply "STOP" anytime and you will be removed from our list and we will stop sending SMS to you.</li>
                <li><strong>Email Communications:</strong> If you subscribe to our email communications, please click the unsubscribe link in any email, and we will remove you from our mailing list.</li>
                <li><strong>Marketing Communications:</strong> You may opt-out of marketing communications while still receiving service-related communications necessary for our business relationship.</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>12. International Data Transfers</h2>
              <p>As we operate globally with delivery centers in India and the United States, your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place for such transfers in accordance with applicable data protection laws.</p>
            </div>

            <div className="privacy-section privacy-closing">
              <p className="closing-statement">
                Thank you for entrusting RKube Inc with your information. We are committed to protecting your privacy and providing a secure and positive experience across all our services including financial services, technology solutions, and business process outsourcing.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
